import { parse, stringify } from 'query-string';
import React from 'react';

import HeadMeta from '@/components/static-pages/HeadMeta';
import LocalityTemplate from '@/components/templates/LocalityTemplate';
import StateLocalityTemplate from '@/components/templates/StateLocalityTemplate';
import { ESearchFilters } from '@/constants/searchFilters';
import { SEO_VEHICLES } from '@/constants/vehicleDetails';
import apiRequest from '@/services/apiRequest';
import { InitSerpGuidedSearchDecision } from '@/services/experiments/serpGuidedSearch';
import { getLocalityPriceAggregation } from '@/services/getLocalityPriceAggregation';
import { getLocalityRentalReviews } from '@/services/getLocalityRentalReviews';
import { getLocalityRentalReviewsAggregations } from '@/services/getLocalityRentalReviewsAggregations';
import getPageData from '@/services/getPageData.js';
import { getCoreApi, getSearchApi } from '@/utility/getCoreApi';

const KEYWORD_DESCRIPTOR = {
  'camper-van': 'camper van rentals',
  airstream: 'Airstream rentals',
  camper: 'camper rentals',
  'sprinter-van': 'Sprinter van rentals',
  'travel-trailer': 'travel trailer rentals',
  cheap: 'cheap RV rentals',
  'class-a': 'Class A RV rentals',
  'class-b': 'Class B RV rentals',
  'class-c': 'Class C RV rentals',
  motorhome: 'motorhome rentals',
  'pet-friendly': 'pet-friendly RV rentals',
  'pop-up-camper': 'pop-up camper rentals',
  small: 'small RV rentals',
  'toy-hauler': 'toy hauler rentals',
  'unlimited-miles': 'RV rentals with unlimited miles',
  westfalia: 'Westfalia camper van rentals',
  default: 'rentals',
};

const Locality = props => {
  const { keyword, metadata } = props;
  const { seo_title, seo_description, locality_type, title, region_code, canonical_url } = metadata;

  const rvType = SEO_VEHICLES[keyword?.slug] ? keyword.title : 'RV';
  const canonicalUrl = (rvType !== 'RV' && keyword?.url) || canonical_url;
  const keywordRentalDescriptor =
    KEYWORD_DESCRIPTOR[keyword?.slug || ''] || KEYWORD_DESCRIPTOR.default;

  const keywordSeoTitle = keyword
    ? `${title} ${keywordRentalDescriptor} - best deals in ${region_code}`
    : '';
  const keywordSeoDescription = keyword
    ? `Discover the best ${keywordRentalDescriptor} in ${title}, ${region_code}! Start your adventure at Outdoorsy, the #1 most trusted RV rental marketplace in the world!`
    : '';
  const seoTitle = keyword ? keywordSeoTitle : seo_title;
  const seoDescription = keyword ? keywordSeoDescription : seo_description;
  return (
    <React.Fragment>
      <HeadMeta
        title={seoTitle}
        description={seoDescription}
        canonicalUrl={canonicalUrl}
        noindex={!!props.fallback}
      />

      {/** Initialize experiments */}
      {locality_type === 'county' && <InitSerpGuidedSearchDecision />}
      {(locality_type === 'state' && <StateLocalityTemplate {...props} />) || (
        <LocalityTemplate {...props} keywordRentalDescriptor={keywordRentalDescriptor} />
      )}
    </React.Fragment>
  );
};

Locality.getInitialProps = async ({ req, res, query }) => {
  const pageData = await getPageData({ req, res, query });
  const { bounds, location, locality_type, region_code } = pageData.metadata;

  let localitySearchFilters = {
    [ESearchFilters.RADIUS]: 10,
    [ESearchFilters.AUTO_EXPAND_RADIUS]: true,
  };

  const activeKeyword = query.keyword
    ? pageData.metadata?.keywords?.find?.(key => key.slug == query.keyword)
    : null;

  if (activeKeyword) {
    pageData.metadata.breadcrumbs = [
      ...pageData.metadata.breadcrumbs,
      {
        title: activeKeyword.title,
        url: activeKeyword.url,
      },
    ];

    localitySearchFilters = {
      ...localitySearchFilters,
      ...activeKeyword.filters,
    };
  }

  const boundsFormatted =
    bounds && pageData.metadata.locality_type === 'county'
      ? {
          'bounds[ne]': `${bounds['ne']['lat']},${bounds['ne']['lon']}`,
          'bounds[sw]': `${bounds['sw']['lat']},${bounds['sw']['lon']}`,
        }
      : null;

  if (boundsFormatted) {
    pageData.metadata.boundsFormatted = boundsFormatted;
  }

  const rentalReviewsLocation = location
    ? {
        lat: location.lat,
        lng: location.lon,
      }
    : null;

  if (rentalReviewsLocation) {
    const localityRentalReviews = await getLocalityRentalReviews(rentalReviewsLocation);
    pageData.metadata.localityRentalReviews = localityRentalReviews;

    const localityPriceAggregation = await getLocalityPriceAggregation(rentalReviewsLocation);
    pageData.metadata.localityPriceAggregation = localityPriceAggregation;
  }

  if (rentalReviewsLocation && locality_type && region_code) {
    const localityRentalReviewsAggregations = await getLocalityRentalReviewsAggregations({
      lat: rentalReviewsLocation.lat,
      lng: rentalReviewsLocation.lng,
      localityType: locality_type,
      regionCode: region_code,
    });
    pageData.metadata.localityRentalReviewsAggregations = localityRentalReviewsAggregations;
  }

  // Alter sections and add search query data to not fetch it on the client
  pageData.metadata.sections = await Promise.all(
    (pageData.metadata.sections || []).map(section => {
      if (section?.section_type === 'search_query' && section?.search_query_url) {
        return new Promise(resolve => {
          try {
            apiRequest({
              url: `${getSearchApi()}/rentals?${stringify({
                ...parse(new URL(section.search_query_url).search),
                remove: 'images',
                raw_json: true,
                omit_aggregation: true,
                [ESearchFilters.PAGE_OFFSET]: 0,
                [ESearchFilters.PAGE_LIMIT]: 4,
                mini: 'true',
              })}`,
            })
              .then(r => resolve({ ...section, search_query_data: r.data }))
              .catch(() => resolve({ ...section, search_query_data: [] }));
          } catch {
            resolve({ ...section, search_query_data: [] });
          }
        });
      }

      return section;
    }),
  );

  // Filter out keywords that have a search query section with the same url.
  pageData.metadata.keywords = (pageData.metadata.keywords || []).filter(k => {
    return !pageData.metadata.sections.some(section => {
      return section?.section_type === 'search_query' && section?.button_url === k.url;
    });
  });

  // Fetch guided search data server-side to prevent client-side pop-in
  let guidedSearchData = null;
  try {
    const guidedSearchResponse = await apiRequest({
      url: `${getCoreApi()}/bui/guided-search`,
    });
    guidedSearchData = guidedSearchResponse;
  } catch (error) {
    // Silently handle guided search data fetch failure
  }

  return {
    ...pageData,
    keyword: activeKeyword,
    localitySearchFilters,
    guidedSearchData,
  };
};

export default Locality;
